import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  QrCode, Gift, TrendingUp, Calendar, ChevronRight,
  Activity, ScanLine, Bell, Loader2, User as UserIcon,
  Heart, Stethoscope, BookOpen, Syringe, Shield, Sparkles,
  Eye, FileText, Video, Music, ArrowRight, Coins, ExternalLink
} from 'lucide-react'
import { pointsApi, eventsApi, settingsApi, authApi, contentApi, getHospitalNameFromCache } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { usePageTitle } from '../../hooks/usePageTitle'
import { useExternalArticle } from '../../hooks/useExternalArticle'

// 事件类型对应的图标和颜色配置（与活动页面一致）
const eventIconConfig: Record<string, { icon: React.ElementType; bg: string; color: string }> = {
  '健康体检': { icon: Stethoscope, bg: 'bg-blue-100', color: 'text-blue-600' },
  '血压测量': { icon: Activity, bg: 'bg-red-100', color: 'text-red-500' },
  '血糖检测': { icon: Heart, bg: 'bg-pink-100', color: 'text-pink-600' },
  '健康讲座': { icon: BookOpen, bg: 'bg-orange-100', color: 'text-orange-500' },
  '疫苗接种': { icon: Syringe, bg: 'bg-purple-100', color: 'text-purple-600' },
}

// 媒体类型图标配置（推荐列表用）
const mediaIconConfig: Record<string, { icon: React.ElementType; bg: string; color: string }> = {
  text: { icon: FileText, bg: 'bg-gradient-to-br from-blue-50 to-blue-100', color: 'text-blue-500' },
  video: { icon: Video, bg: 'bg-gradient-to-br from-purple-50 to-purple-100', color: 'text-purple-500' },
  audio: { icon: Music, bg: 'bg-gradient-to-br from-orange-50 to-orange-100', color: 'text-orange-500' },
}

// 模块级锁：刷新窗口内只请求一次系统设置
let _sysSettingsLoaded = false

interface PointRecord {
  id: string
  eventType: string
  points: number
  status: string
  createdAt: string
  hospital: string
  totalPoints: number
}

interface EventType {
  id: string
  name: string
  description: string
  points: number
  enabled: boolean
}

const UserHome = () => {
  usePageTitle()
  // 外链文章跳转：公众号链接直跳，其他外链在微信内引导复制（见 lib/externalLink.ts）
  const { openLink, dialog: externalDialog } = useExternalArticle()
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const [user, setUser] = useState<any>(null)
  const [recentRecords, setRecentRecords] = useState<PointRecord[]>([])
  const [enabledEvents, setEnabledEvents] = useState<EventType[]>([])
  const [loading, setLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)
  const [recommended, setRecommended] = useState<any[]>([])

  useEffect(() => {
    window.scrollTo(0, 0)

    ;(async () => {
      // 从存储中恢复用户信息（优先 auth_user，兼容 userAccount）
      const storedAuthUser = (() => {
        try {
          const raw = localStorage.getItem('auth_user')
          return raw ? JSON.parse(raw) : null
        } catch { return null }
      })()
      const storedAccount = (() => {
        try {
          const raw = localStorage.getItem('userAccount')
          return raw ? JSON.parse(raw) : null
        } catch { return null }
      })()

      // 合并数据：优先用 auth_user 的 token 和 role，userAccount 的详细信息
      const mergedUser = storedAccount
        ? { ...storedAuthUser, ...storedAccount }
        : storedAuthUser

      if (mergedUser) {
        setUser(mergedUser)
      } else if (authUser) {
        setUser(authUser)
      }

      // 读取未读消息数
      if (storedAccount) {
        setUnreadCount(storedAccount.unreadCount ?? 0)
      }

      // 加载系统设置到 localStorage（仅请求一次）
      if (!_sysSettingsLoaded) {
        _sysSettingsLoaded = true
        try {
          const sys = await settingsApi.getSystem()
          if (sys) {
            if (sys.systemName) localStorage.setItem('systemName', sys.systemName)
            if (sys.systemTitle) localStorage.setItem('systemTitle', sys.systemTitle)
            if (sys.copyright) localStorage.setItem('copyright', sys.copyright)
            if (sys.systemLogo) localStorage.setItem('systemLogo', sys.systemLogo)
          }
        } catch {
          _sysSettingsLoaded = false
        }
      }

      // 加载事件和积分
      // 推荐内容
      let recs: any[] = []
      try {
        const recResp = await contentApi.getRecommended({ limit: 5 })
        recs = recResp.items || []
      } catch (e) {
        recs = []
      }
      setRecommended(recs)

      try {
        const eventsResp = await eventsApi.list({ is_active: true }) as any
        const evtsRaw = eventsResp.event_types || eventsResp.items || eventsResp || []
        const evts = evtsRaw.map((e: any) => ({
          id: e.id,
          name: e.name,
          description: e.description || '',
          points: e.points,
          enabled: e.enabled === 1 || e.enabled === true,
        }))
        setEnabledEvents(evts.filter((e: EventType) => e.enabled).slice(0, 4))

        // 积分记录需要登录后才请求
        const currentToken = localStorage.getItem('token')
        if (currentToken) {
          const recordsResp = await pointsApi.records({ page_size: 20 }) as any
          const items = recordsResp.records || recordsResp.items || recordsResp || []
          const mapped: PointRecord[] = items.map((r: any) => ({
            id: r.id,
            eventType: r.event_type?.name || r.event_type_name || '未知',
            points: r.points,
            status: r.status,
            createdAt: r.created_at ? new Date(r.created_at).toLocaleString('zh-CN') : '',
            hospital: r.hospital_name || '',
            totalPoints: r.balance ?? 0,
          }))
          setRecentRecords(mapped.filter(r => r.status === 'confirmed').slice(0, 3))
        }
      } catch (e) {
        console.error('Failed to load home data', e)
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const getCachedPoints = () => {
    if (authUser?.points) return authUser.points
    try {
      const stored = localStorage.getItem('userAccount')
      if (stored) {
        const parsed = JSON.parse(stored)
        return parsed.points ?? 0
      }
    } catch { /* ignore */ }
    return 0
  }
  const cachedPoints = getCachedPoints()
  const userPoints = user?.points ?? cachedPoints
  const isLoggedIn = !!(authUser || localStorage.getItem('token') || user)

  // 快捷入口统一配置（消除登录态重复分支）
  const quickLinks = [
    { to: '/user/scan', label: '扫码', Icon: ScanLine, bg: 'bg-gradient-to-br from-green-50 to-green-100', color: 'text-green-600' },
    { to: '/user/points', label: '积分明细', Icon: TrendingUp, bg: 'bg-gradient-to-br from-blue-50 to-blue-100', color: 'text-blue-600' },
    { to: '/user/exchange', label: '积分兑换', Icon: Gift, bg: 'bg-gradient-to-br from-orange-50 to-orange-100', color: 'text-orange-600' },
    { to: '/user/profile', label: '我的', Icon: UserIcon, bg: 'bg-gradient-to-br from-purple-50 to-purple-100', color: 'text-purple-600' },
  ]

  return (
    <>
    {/* 外链跳转引导弹窗（仅「微信内 + 非公众号链接」时出现） */}
    {externalDialog}
    <div className="min-h-screen bg-gray-50 pb-4">
      {/* 顶部 Hero：渐变绿 + 装饰 */}
      <div className="relative bg-gradient-to-br from-green-700 via-green-600 to-emerald-500 overflow-hidden">
        <div className="absolute right-[-30px] top-[-20px] w-36 h-36 rounded-full bg-white/10 animate-soft-pulse" />
        <div className="absolute right-14 top-9 w-16 h-16 rounded-full bg-white/10 animate-soft-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute left-[28%] bottom-[-24px] w-24 h-24 rounded-full bg-emerald-300/20" />
        <div className="absolute right-[24%] bottom-4 text-white/10 rotate-12">
          <Heart className="w-14 h-14" fill="currentColor" />
        </div>

        <div className="relative z-10 px-4 pt-6 pb-14">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3.5 min-w-0">
              {/* 头像圆标 */}
              <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/25 flex items-center justify-center flex-shrink-0 shadow-sm">
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-xl object-cover" />
                ) : (
                  <span className="text-lg font-bold text-white">{isLoggedIn ? (user?.name?.[0] || '用') : '游'}</span>
                )}
              </div>
              <div className="min-w-0">
                {isLoggedIn ? (
                  <>
                    <h2 className="text-lg font-bold text-white truncate">你好，{user?.name || '用户'}</h2>
                    {(user?.hospitalName || getHospitalNameFromCache(user?.hospitalId)) ? (
                      <p className="text-green-100 text-xs mt-0.5 flex items-center truncate">
                        <Shield className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{user.hospitalName || getHospitalNameFromCache(user?.hospitalId)}</span>
                      </p>
                    ) : (
                      <p className="text-green-100 text-xs mt-0.5">坚持健康生活，积分越多礼品越丰富哦~</p>
                    )}
                  </>
                ) : (
                  <>
                    <h2 className="text-lg font-bold text-white">欢迎访问</h2>
                    <p className="text-green-100 text-xs mt-0.5">登录后即可参与活动、兑换礼品</p>
                  </>
                )}
              </div>
            </div>
            {isLoggedIn ? (
              <Link to="/user/notifications" className="relative p-2.5 rounded-full bg-white/15 border border-white/20 text-white hover:bg-white/25 transition-colors flex-shrink-0">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full border-2 border-green-600" />
                )}
              </Link>
            ) : (
              <Link to="/user/login" className="px-4 py-2 bg-white text-green-700 hover:bg-green-50 text-sm font-medium rounded-full shadow-sm transition-colors flex-shrink-0">
                登录
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* 积分卡片 */}
      <div className="px-4 -mt-9 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* 积分主区 */}
          <div className="px-4 py-3.5 bg-gradient-to-br from-green-50/80 to-transparent">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] text-gray-500 flex items-center gap-1">
                  <Coins className="w-3 h-3 text-green-500" />当前积分
                </p>
                {isLoggedIn ? (
                  <p className="text-[32px] leading-tight font-bold bg-gradient-to-r from-green-600 to-emerald-500 bg-clip-text text-transparent mt-0.5">
                    {userPoints}
                    <span className="text-sm text-gray-400 ml-1 font-medium">分</span>
                  </p>
                ) : (
                  <p className="text-2xl font-bold text-gray-300 mt-0.5">--</p>
                )}
              </div>
              <div className="flex space-x-2">
                {isLoggedIn ? (
                  <>
                    <Link to="/user/scan" className="flex flex-col items-center px-3 py-2 bg-white rounded-xl border border-green-100 hover:border-green-300 hover:shadow-sm transition-all">
                      <QrCode className="w-5 h-5 text-green-600" />
                      <span className="text-[10px] text-green-700 mt-0.5 font-medium">身份码</span>
                    </Link>
                    <Link to="/user/points" className="flex flex-col items-center px-3 py-2 bg-white rounded-xl border border-blue-100 hover:border-blue-300 hover:shadow-sm transition-all">
                      <Activity className="w-5 h-5 text-blue-600" />
                      <span className="text-[10px] text-blue-700 mt-0.5 font-medium">积分明细</span>
                    </Link>
                  </>
                ) : (
                  <Link to="/user/login" className="flex flex-col items-center px-3 py-2 bg-white rounded-xl border border-green-100 hover:border-green-300 hover:shadow-sm transition-all">
                    <QrCode className="w-5 h-5 text-green-600" />
                    <span className="text-[10px] text-green-700 mt-0.5 font-medium">登录查看</span>
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* 最新积分记录 */}
          <div className="px-4 pb-3.5 pt-2.5 border-t border-gray-100">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] font-semibold text-gray-700 flex items-center gap-1">
                <span className="w-1 h-3 bg-gradient-to-b from-green-500 to-emerald-400 rounded-full inline-block" />
                最新积分记录
              </p>
              {isLoggedIn ? (
                <Link to="/user/points" className="text-[11px] text-green-600 hover:text-green-700 flex items-center">
                  全部<ChevronRight className="w-3 h-3" />
                </Link>
              ) : (
                <span className="text-[11px] text-gray-400">登录后查看</span>
              )}
            </div>
            <div className="space-y-2">
              {loading ? (
                <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-gray-300" /></div>
              ) : isLoggedIn ? (
                recentRecords.length > 0 ? (
                  recentRecords.map((record) => (
                    <div key={record.id} className="flex items-center py-0.5">
                      <div className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center mr-2 flex-shrink-0">
                        <Activity className="w-3.5 h-3.5 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-gray-800 truncate">{record.eventType}</p>
                        <p className="text-[10px] text-gray-400">{record.createdAt}</p>
                      </div>
                      <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full ml-2">+{record.points}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-400 text-center py-3">暂无积分累计记录，参与活动即可赚取积分</p>
                )
              ) : (
                <div className="text-center py-3">
                  <p className="text-xs text-gray-400 mb-1.5">登录后查看您的积分记录</p>
                  <Link to="/user/login" className="inline-flex items-center gap-1 text-[13px] text-green-600 hover:text-green-700 font-medium">
                    立即登录 <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 快捷功能 */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-3 py-3">
          <div className="grid grid-cols-4 gap-3">
            {quickLinks.map(({ to, label, Icon, bg, color }) => (
              <Link key={label} to={isLoggedIn ? to : '/user/login'} className="group flex flex-col items-center text-center">
                <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center mb-1 group-hover:scale-105 group-hover:shadow-sm transition-all`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <span className="text-[11px] text-gray-600 group-hover:text-gray-800 transition-colors">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* 为你推荐 */}
      {recommended.length > 0 && (
        <div className="px-4 mt-4">
          <div className="flex items-center justify-between mb-2.5 px-1">
            <h3 className="text-[13px] font-bold text-gray-800 flex items-center gap-1.5">
              <span className="w-1 h-3.5 bg-gradient-to-b from-green-500 to-emerald-400 rounded-full inline-block" />
              为你推荐
            </h3>
            <Link to="/user/knowledge" className="text-[11px] text-green-600 hover:text-green-700 flex items-center">
              更多<ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-2 py-1.5">
            <div className="space-y-0.5">
              {recommended.map((c: any) => {
                const mcfg = mediaIconConfig[c.media_type] || mediaIconConfig.text
                const MIcfg = mcfg.icon
                // 外链文章：点击直接跳转，不进详情页（规则见 lib/externalLink.ts）
                const external = String(c.link_url || '').trim()
                const cardCls = 'group flex items-center gap-2.5 p-1.5 rounded-xl hover:bg-green-50/60 transition-colors'
                const inner = (
                  <>
                    {c.cover_url ? (
                      <img src={c.cover_url} alt={c.title} className="w-11 h-11 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                    ) : (
                      <div className={`w-11 h-11 rounded-lg ${mcfg.bg} flex items-center justify-center flex-shrink-0`}>
                        <MIcfg className={`w-5 h-5 ${mcfg.color}`} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-gray-800 line-clamp-1 group-hover:text-green-700 transition-colors">{c.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{c.category_name || '科普'}</span>
                        {external && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 inline-flex items-center gap-0.5">
                            <ExternalLink className="w-2.5 h-2.5" />外链
                          </span>
                        )}
                        {c.view_count !== undefined && (
                          <span className="text-[10px] text-gray-400 inline-flex items-center gap-0.5">
                            <Eye className="w-2.5 h-2.5" />{c.view_count}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-green-500 transition-colors flex-shrink-0" />
                  </>
                )
                return external ? (
                  <button key={c.id} type="button" onClick={() => openLink(external)} className={`w-full text-left ${cardCls}`}>
                    {inner}
                  </button>
                ) : (
                  <Link key={c.id} to={`/user/knowledge/${c.id}`} className={cardCls}>
                    {inner}
                  </Link>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* 积分获取攻略 */}
      <div className="px-4 mt-4">
        <div className="rounded-2xl px-3.5 py-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-green-500" />
            </div>
            <div>
              {isLoggedIn ? (
                <>
                  <p className="font-bold text-green-700 text-[13px]">积分获取攻略</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">参与健康活动可获积分，兑换丰富礼品</p>
                </>
              ) : (
                <>
                  <p className="font-bold text-green-700 text-[13px]">登录即可参与活动</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">参与健康活动获取积分，兑换丰富礼品</p>
                </>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate(isLoggedIn ? '/user/activities' : '/user/login')}
            className="p-1.5 rounded-full bg-green-600 hover:bg-green-700 hover:scale-105 transition-all flex-shrink-0 shadow-sm"
            aria-label="查看活动"
          >
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>

      {/* 热门活动 */}
      <div className="px-4 mt-4 mb-4">
        <div className="flex items-center justify-between mb-2.5 px-1">
          <h3 className="text-[13px] font-bold text-gray-800 flex items-center gap-1.5">
            <span className="w-1 h-3.5 bg-gradient-to-b from-green-500 to-emerald-400 rounded-full inline-block" />
            热门活动
          </h3>
          <Link to="/user/activities" className="text-[11px] text-green-600 hover:text-green-700 flex items-center">
            更多<ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-300" /></div>
          ) : enabledEvents.length > 0 ? (
            enabledEvents.map((event) => {
              const cfg = eventIconConfig[event.name] ?? {
                icon: Calendar,
                bg: 'bg-green-100',
                color: 'text-green-600',
              }
              const IconComp = cfg.icon
              return (
                <Link
                  key={event.id}
                  to="/user/activities"
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-green-200 transition-all px-3 py-2.5 flex items-center group"
                >
                  <div className={`w-10 h-10 ${cfg.bg} rounded-xl flex items-center justify-center mr-2.5 flex-shrink-0 group-hover:scale-105 transition-transform`}>
                    <IconComp className={`w-5 h-5 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 text-[13px]">{event.name}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-1">{event.description}</p>
                  </div>
                  <div className="text-right ml-2.5 flex-shrink-0">
                    <span className="inline-block text-sm font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">+{event.points}</span>
                    <p className="text-[10px] text-gray-400 mt-0.5">积分</p>
                  </div>
                </Link>
              )
            })
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 p-4 text-center">
              <p className="text-xs text-gray-400">暂无可用活动，敬请期待</p>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 pb-6 text-center">
        <p className="text-xs text-gray-400">{localStorage.getItem('copyright') || '© 2026 眉山疾控'}</p>
        {localStorage.getItem('icpNumber') && (
          <p className="text-xs text-gray-400 mt-1">{localStorage.getItem('icpNumber')}</p>
        )}
      </div>

    </div>
    </>
  )
}

export default UserHome
